import React, { CSSProperties, ReactNode, useCallback } from 'react';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { IconName } from '../Icon/SystemIcon';
import { Button } from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';

export interface GroupButtonConfig<T extends string> {
  callback: (id: T, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  tooltip?: string;
  icon?: () => ReactNode;
  iconName?: IconName;
  id: T;
  label?: string;
  selected: boolean;
  disabled?: boolean;
}

interface Props<T extends string> {
  buttonConfig: GroupButtonConfig<T>;
  numSiblings: number;
  index: number;
  theme: 'default' | 'grey';
  paddingRem?: number;
}

export function GroupButton<T extends string>({ buttonConfig, index, numSiblings, theme }: Props<T>) {
  const { tooltip, icon, id, callback, selected, label, disabled } = buttonConfig;
  const last: boolean = index === numSiblings - 1;
  const first: boolean = index === 0;

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      callback(id, event);
    },
    [callback, id]
  );

  const styles: CSSProperties = {
    border: 'none',
    borderRadius: '0 0 0 0',
    cursor: !selected ? 'pointer' : 'unset',
    pointerEvents: selected ? 'none' : 'unset',
    padding: '5px 15px',
    ...(selected ? { border: 'none' } : {}),
    ...(first ? { borderRadius: '2px 0 0 2px' } : {}),
    ...(last ? { borderRadius: '0 2px 2px 0' } : {}),
    ...(!last ? { borderRight: `thin solid ${tokens.colorSurfaceVariant}` } : {})
  };

  const getIconAndLabel = (anIcon?: () => ReactNode, aLabel?: string) => {
    const nodes: ReactNode[] = [];
    if (anIcon) {
      nodes.push(anIcon());
    }
    if (aLabel && aLabel !== '') {
      nodes.push(aLabel);
    }
    return nodes;
  };

  return (
    <Tooltip title={tooltip ?? ''}>
      <Button
        variant={selected && (theme === 'default' || theme === 'grey') ? 'contained' : 'outlined'}
        color={selected && theme === 'default' ? 'primary' : 'default'}
        style={styles}
        disabled={disabled}
        dataTest={`group__button__${id}`}
        onClick={onClick}
        data-selected={selected ? 'true' : 'false'}
      >
        {getIconAndLabel(icon, label)}
      </Button>
    </Tooltip>
  );
}

export default GroupButton;
